import type { Locale } from "next/router";

import dayjs from "dayjs";
// One import per site locale. dayjs falls back to English for any locale
// that was never registered, silently: `.locale("es")` on an unregistered
// locale does not throw, it formats in English. es and it were added to
// the site after this list and never made it in, so every
// `formatDateWithLocale` call rendered "17 July 2026" on those two.
import "dayjs/locale/de";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/nl";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { formatMoney } from "../../../config/price-format";
import type { PricePlace } from "../../../config/price-format";
import { Currency, Price } from "@/Apollo/schema";
import { LISTING_CURRENCY } from "@/common/static/currency.static";
import { toContentLocale } from "@/i18n/config";
import removeLocaleFromPath from "@/utils/removeLocaleFromPath";

dayjs.extend(localizedFormat);

/**
 * What `formatPrice` reads from a price: the amount and its currency. Kept
 * structural rather than `Partial<Price>` because a selection of the
 * gateway's `Price` can nest (`unconverted` is itself a Price, WHO-5997) and
 * a shallow Partial rejects the deep-partial shapes codegen produces for it.
 */
export type FormatPricePrice =
  | { amount?: number | null; currency?: Currency | null }
  | number
  | null;

export type FormatPriceOptions = {
  currency?: Currency;
  /** Falls back to `en`. `useFormatPrice` fills it from the router. */
  locale?: Locale | string;
  /** Where the price is shown (price display policy); `"exact"` when omitted. */
  place?: PricePlace;
};

/**
 * Formats a price with the site's one money formatter (config/price-format.js).
 * Hides zero and missing amounts, which is what the shop surfaces using it
 * want (no "€0" badge on an ad without a price). Call `formatMoney` directly
 * where a zero must show.
 *
 * @param price Either Price object (with amount and/or currency) or amount to format.
 * @param options Currency, locale and place.
 */
export const formatPrice = (
  price?: FormatPricePrice,
  options?: FormatPriceOptions
) => {
  if (!price) {
    return "";
  }

  const amount = typeof price === "number" ? price : price.amount;

  if (!amount) {
    return "";
  }

  const currency =
    typeof price === "number"
      ? options?.currency ?? LISTING_CURRENCY
      : price.currency ?? options?.currency ?? LISTING_CURRENCY;

  return formatMoney(amount, {
    locale: options?.locale,
    currency,
    place: options?.place,
  });
};

/**
 * Sets where a price is shown before handing it to `{{price, currency}}`:
 * checkout passes `"money"` (`€925.00`, Dutch `€ 925,-`), a card `"list"`.
 */
export const withPricePlace = <T extends object>(
  price: T | null | undefined,
  place: PricePlace
) => (price ? { ...price, place } : price);

/**
 * A currency's own symbol in the locale (€, £, $), for an amount input's adornment, so the
 * input shows the currency the amount is entered in (WHO-5142).
 */
export const getCurrencySymbol = (
  currency: Currency,
  locale?: Locale | string
): string =>
  new Intl.NumberFormat(locale || "en", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  })
    .formatToParts(0)
    .find(part => part.type === "currency")?.value ?? currency;

/**
 * dayjs has a file per content language and none for `en-us`; given a locale
 * it doesn't know, it keeps the global one (which the chat sets), so `en-us`
 * would format in whatever language was used last. `en-us` reads `en` (WHO-5766).
 */
export const formatDateWithLocale = (
  value: string,
  format: string,
  locale: Locale
) => {
  return dayjs(value).locale(toContentLocale(locale)).format(format);
};

const splitLocale = (locale: string) => {
  const [language = locale, region] = locale.split("-");
  return { language, region: region?.toUpperCase() };
};

/**
 * A deadline that ends at an exact moment, with its time and the viewer's time zone:
 * "Nov 10, 3:00 PM EST" on `/en-us`. `Intl` rather than {@link formatDateWithLocale}: dayjs
 * prints no zone without its timezone plugin. `Intl` takes the routed locale as it is, so
 * `en-us` formats as US English. `timeZone` is for tests; the page uses the browser's.
 */
export const formatDateTimeWithZone = (
  value: string,
  locale: Locale | undefined,
  timeZone?: string
): string =>
  new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).format(new Date(value));

/**
 * Returns the display name of a language in its own locale. A regional
 * locale keeps its language name with the region code beside it, so `en-us`
 * reads "English (US)" rather than the dialect name "American English".
 * Built from the plain language name so it reads the same in every browser,
 * including those without `languageDisplay` support (iOS before 15.4).
 */
export const getLanguageName = (locale: string): string => {
  const { language, region } = splitLocale(locale);
  const displayNames = new Intl.DisplayNames([locale], { type: "language" });
  const name = displayNames.of(language) ?? language;
  return region ? `${name} (${region})` : name;
};

/**
 * Short label for the language picker's button: "EN", "NL", or "EN (US)"
 * for a regional locale.
 */
export const getLocaleLabel = (locale: string): string => {
  const { language, region } = splitLocale(locale);
  const code = language.toUpperCase();
  return region ? `${code} (${region})` : code;
};

type LocaleUrls = Partial<Record<string, string>>;

/**
 * Resolves the path and source locale for a given locale when switching languages.
 *
 * If locale-specific URLs are provided (e.g., from a CMS), it uses the URL for the target locale.
 * Otherwise, it falls back to removing the locale prefix from the current path.
 *
 * @param locale - The target locale to switch to
 * @param urls - Optional map of locale-specific URLs (e.g., { en: '/about', nl: '/over-ons' })
 * @param currentPath - The current router path (router.asPath)
 *
 * @returns An object containing the resolved path and optional source locale
 */
export const resolveLocalePath = (
  locale: Locale,
  urls: LocaleUrls,
  currentPath: string
): { path: string; sourceLocale: Locale | undefined } => {
  const localeUrl = urls[locale];

  if (localeUrl) {
    return {
      path: localeUrl,
      sourceLocale: locale,
    };
  }

  return {
    path: removeLocaleFromPath(currentPath),
    sourceLocale: undefined,
  };
};
